import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  FileBarChart,
  FileCheck,
  CheckCircle,
  AlertTriangle,
  Shield,
  ArrowRight,
  TrendingUp,
  BarChart3,
  Clock,
  Users,
  DollarSign,
  Layers,
  RefreshCw,
  Target,
  Layout,
  Scale,
  BookOpen
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const ContractsLanding = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const contractCards = [
    // Phase 1: Setup & Configuration
    {
      id: 'pre-contract',
      title: 'Pre-Contract Compliance',
      description: 'Configure compliance requirements & approval workflows',
      icon: <Shield size={24} />,
      path: '/contracts/pre-contract',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Type Configuration', 'Pricing Models', 'Workflow Setup']
    },
    {
      id: 'contract-type-management',
      title: 'Contract Type Management',
      description: 'Configure contract types, pricing models & templates',
      icon: <Layers size={24} />,
      path: '/contracts/types/management',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Type Configuration', 'Pricing Models', 'Workflow Setup']
    },
    {
      id: 'contract-templates',
      title: 'Contract Templates',
      description: 'Standardized templates & version control',
      icon: <FileCheck size={24} />,
      path: '/contracts/templates',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Template library', 'Version control', 'Standardization']
    },
    {
      id: 'contract-management',
      title: 'Contract Management',
      description: 'Comprehensive contract lifecycle management',
      icon: <FileText size={24} />,
      path: '/contracts/management',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Contract tracking', 'Document management', 'Contract details']
    },
    {
      id: 'fixed-price-contracts',
      title: 'Fixed-Price Contracts',
      description: 'Fixed-price contracts with scope, deliverables & milestones',
      icon: <Target size={24} />,
      path: '/contracts/fixed-price',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Scope Tracking', 'Milestone Management', 'Deliverables']
    },
    {
      id: 'legal-hold',
      title: 'Legal Hold',
      description: 'Lock contracts during disputes or audits',
      icon: <Scale size={24} />,
      path: '/contracts/legal-hold',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Lock Contracts', 'Audit History', 'Dispute Management']
    },
    {
      id: 'time-materials-contracts',
      title: 'Time & Materials',
      description: 'T&M contracts with hourly rates, time tracking & costs',
      icon: <Clock size={24} />,
      path: '/contracts/time-materials',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Time Tracking', 'Hourly Rates', 'Cost Management']
    },
    // Phase 2: Creation & Execution
    {
      id: 'contract-lifecycle',
      title: 'Contract Lifecycle',
      description: 'End-to-end contract lifecycle from draft to execution',
      icon: <FileBarChart size={24} />,
      path: '/contracts/lifecycle',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Drafting', 'Review', 'Approval', 'Execution']
    },
    {
      id: 'e-signature',
      title: 'E-Signature',
      description: 'Digital signature integration & management',
      icon: <CheckCircle size={24} />,
      path: '/contracts/e-signature',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['DocuSign', 'Adobe Sign', 'Digital workflow']
    },
    // Phase 3: Monitoring & Compliance
    {
      id: 'renewal-alerts',
      title: 'Contract Renewals & Alerts',
      description: 'Unified renewal planning, configurable thresholds, and alert workflows',
      icon: <AlertTriangle size={24} />,
      path: '/contracts/renewals-alerts',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Renewal planning', 'Threshold alerts', 'Auto-renew controls']
    },
    {
      id: 'compliance-tracking',
      title: 'Compliance Tracking',
      description: 'Obligation & compliance monitoring',
      icon: <Shield size={24} />,
      path: '/contracts/compliance',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Obligation tracking', 'Compliance monitoring', 'Risk assessment']
    },
    {
      id: 'regulatory-frameworks',
      title: 'Regulatory Frameworks',
      description: 'Master data for regulations (GDPR, SOX) & control requirements',
      icon: <BookOpen size={24} />,
      path: '/contracts/frameworks',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Framework Library', 'Requirement Mapping', 'Audit Standards']
    },
    {
      id: 'obligations-dashboard',
      title: 'Obligations Dashboard',
      description: 'Centralized master view of all contract obligations and risks',
      icon: <Layout size={24} />,
      path: '/contracts/obligations',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Global Monitoring', 'Risk Overview', 'Status Filters']
    },
    {
      id: 'contract-analytics',
      title: 'Contract Analytics',
      description: 'Analytics & reporting for contract management',
      icon: <BarChart3 size={24} />,
      path: '/contracts/analytics',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Performance metrics', 'Trend analysis', 'Custom reports']
    },
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  // Filter contracts based on search query
  const filteredCards = contractCards.filter(card =>
    card.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* Breadcrumb + Search Bar */}
      <div
        className="d-flex justify-content-between align-items-center mb-5"
        style={{ gap: '16px', flexWrap: 'wrap' }}
      >
        <div style={{ flex: '1 1 auto' }}>
          <PageBreadcrumb
            style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              fontWeight: '500'
            }}
          />
        </div>

        <div style={{ flex: '0 0 auto' }}>
          <input
            type="text"
            placeholder="Search Modules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 16px',
              borderRadius: '10px',
              border: '1px solid #D1D5DB',
              width: '320px',
              fontSize: '14px',
              outline: 'none',
              transition: '0.2s',
            }}
          />
        </div>
      </div>

      {/* Page Header */}
      <PageHeader
        title="Contracts Management"
        description="Comprehensive contract lifecycle management and tracking system"
      />

      <div className="page-container">
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {filteredCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%'
                }}
              />
              <div style={{ padding: '24px' }}>
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg || '#F3E7FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: card.iconLine || '#8A3FC5' }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight
                    size={20}
                    className="asset-card-arrow"
                  />
                </div>
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {card.description}
                </p>
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ContractsLanding;